import * as React from 'react';
import { useLocation } from '@reach/router';
import * as yup from 'yup';
import { ApolloError } from '@apollo/client';
import _ from 'lodash';

import {
  WizardProps,
  WizardContextValues,
  StepHandler,
  WizardStepData,
  WizardStepProps,
  OnNextFn,
} from './WizardTypes';
import WizardContext from './WizardContext';
import { logger } from '$/utils/logger';
import Modal from '../Modal';
import { css } from '@emotion/react';
import { FieldValues, useFormContext } from 'react-hook-form';
import { motion } from 'framer-motion';
import ApolloForm, { buildSubmitError } from '$/components/ApolloForm';
import { FormProps } from '../Form';
import { useHasChangedShallow } from '$/hooks/useHasChanged';

import Wizard from './Wizard';
import config from '$/config';
import useBrowserStorage from '$/components/BrowserStorage';
import useRunOnce from '$/hooks/useRunOnce';
import { Portal } from '$/components/Portal';
import { useMemoized } from '$/hooks/useMemoized';
import { navigate } from 'gatsby';
import { useRefFromState } from '$/hooks/useRefFromState';

export const useWizard = (): WizardContextValues => {
  const context = React.useContext(WizardContext);

  if (!context && !config.isProduction) {
    throw Error('Wrap your step with `Wizard`');
  } else {
    return context as WizardContextValues;
  }
};

// Extend React.ReactElement to enforce type safety on the children prop
type WizardElement = React.ReactElement<WizardStepProps>;

const useWizardSteps = (
  children: React.ReactNode // Use React.ReactNode to allow for single elements, arrays, and more
): WizardElement[] => {
  const steps = React.useMemo(() => {
    function findInChildren(children: React.ReactNode): WizardElement[] {
      // Convert children to a manageable array, handling single or nested children structures
      const childArray = React.Children.toArray(children) as WizardElement[];
      for (const child of childArray) {
        if (React.isValidElement<WizardStepProps>(child)) {
          // Check if the current child has the matching ID
          if (child.props.id === 'wizard-steps') {
            // If found, return its children, ensuring they're cast as an array of ReactElement
            return React.Children.toArray(child.props.children) as WizardElement[];
          } else if (child.props.children) {
            // If not, and the child has children, search its children recursively
            const found = findInChildren(child.props.children);
            if (found.length > 0) {
              // If a matching child is found in the subtree, return the result
              return found;
            }
          }
        }
      }
      // If no matching child is found in this branch, return an empty array
      return [];
    }

    // Start searching from the root children
    return findInChildren(children);
  }, [children]); // Depend on the `children` prop

  return steps;
};

const AnimateStep: React.FC<
  React.PropsWithChildren<{
    path: string;
    index: number;
  }>
> = ({ index, path, children }) => {
  const wizard = useWizard();
  const isActive = wizard.activeStep === index;

  const animation = React.useMemo(() => {
    return isActive
      ? {
          opacity: 1,
          translateX: '0%',
          transition: { delay: 0.1 },
          transitionEnd: { display: 'flex' },
        }
      : {
          opacity: 0,
          translateX: wizard.activeStep > index ? '-10%' : '10%',
          transitionEnd: { display: 'none' },
        };
  }, [index, isActive, wizard.activeStep]);

  return (
    <motion.div
      id={`wizard-step-${path?.replace('/', '') ?? 'default'}`}
      className='wizard-step w-full justify-center'
      style={{
        opacity: isActive ? 1 : 0,
        translateX: isActive ? '0%' : '10%',
        display: isActive ? 'flex' : 'none',
      }}
      animate={animation}
    >
      {children}
    </motion.div>
  );
};

type UpdateFormFn = (
  rules: yup.ObjectSchema<any> | undefined,
  onNext: OnNextFn | undefined
) => void;

export const WizardLogic: React.FC<
  React.PropsWithChildren<
    WizardProps & {
      updateWizardForm: UpdateFormFn;
    }
  >
> = ({
  container,
  before,
  after,
  onEndNavigateTo: endNavigateTo,
  onEnd: onEndNavigate,
  base,
  children,
  updateWizardForm,
  className,
  startIndex = 0,
  baseIndex = 0,
  inModal,
  defaultStepData,
}) => {
  const browserStorage = useBrowserStorage();

  const steps = useWizardSteps(children);

  const [activeStep, setActiveStepState] = React.useState(startIndex);
  const stepCount = steps.length;
  const activeChild = steps[activeStep] as React.ReactElement<WizardStepProps>;
  const activePath = activeChild.props.path;

  const [stepData, setStepData] = React.useState<WizardStepData | undefined>(defaultStepData);
  const [isSubmitting, setIsSubmitting] = React.useState(false);
  const hasNextStep = React.useRef(true);
  const hasPreviousStep = React.useRef(false);
  const jumpToPathCallBack = React.useRef<(() => void) | undefined>(undefined);

  const handlers = React.useRef<Record<string, StepHandler>>({});

  const location = useLocation();
  const pathName = location.pathname;
  const pathNameRef = useRefFromState(pathName);
  const { trigger, getValues, setValue, setError, formState, clearErrors } = useFormContext();

  const reset = React.useCallback(() => {
    browserStorage.wizardStore = {};
    browserStorage.save();
    setValue('root', {});
    setActiveStepState(startIndex);
  }, [browserStorage, setValue, startIndex]);

  const saveState = React.useCallback(() => {
    browserStorage.wizardStore = {
      base,
      lastPath: activePath,
      formData: getValues(),
    };
    delete browserStorage.wizardStore.formData?.password;
    logger.debug('WIZARD', 'Saving state', browserStorage.wizardStore);
    browserStorage.save();
  }, [activePath, base, browserStorage, getValues]);

  const verifyStep = React.useCallback(
    async (fields?: string[]): Promise<boolean> => {
      const valid = await trigger(fields);
      if (!valid) {
        logger.debug('WIZARD', `Verifying ${!fields ? 'all' : fields} fields = ERROR`, {
          values: getValues(),
        });
        setIsSubmitting(false);
        return valid;
      }

      logger.debug('WIZARD', `Verifying ${!fields ? 'all' : fields} fields = VALID`);
      return valid;
    },
    [getValues, trigger]
  );

  const activeStepRef = useRefFromState(activeStep);
  const stepsRef = useRefFromState(steps);
  const setActiveStep = React.useCallback(
    (newIndex: number) => {
      (async function () {
        setIsSubmitting(true);

        const goingForward = newIndex >= activeStepRef.current;
        logger.debug(
          'WIZARD',
          `Trying to change activeStep ${goingForward ? 'forward' : 'backward'} to ${newIndex}`
        );
        if (goingForward) {
          if (!(await verifyStep())) {
            setIsSubmitting(false);
            return false;
          }
        }

        const newStep = stepsRef.current[newIndex] as React.ReactElement<WizardStepProps>;
        const newHandler = handlers.current[newStep.props.path];
        if (!newHandler) {
          throw new Error(
            `No handler found for path ${newStep.props.path}.  You must call registerHandler`
          );
        }

        if (newHandler.shouldSkipStep) {
          const skip = await newHandler.shouldSkipStep();
          if (skip) {
            const skipIndex = goingForward ? newIndex + 1 : newIndex - 1;
            if (skipIndex < 0 || skipIndex >= stepCount) {
              // Can't skip
              setIsSubmitting(false);
              return;
            }
            setActiveStep(skipIndex);
            return;
          }
        }

        hasNextStep.current = newIndex < stepCount - 1;
        hasPreviousStep.current = newIndex > startIndex;

        // Save this in state and in localStorage
        setActiveStepState(newIndex);
        return true;
      })();
    },
    [activeStepRef, startIndex, stepCount, stepsRef, verifyStep]
  );

  const finishFlow = React.useCallback(async () => {
    // Clear out saved state
    browserStorage.wizardStore = {};
    browserStorage.save();
  }, [browserStorage]);

  const formHandlers = useFormContext();
  const goToNextStep = React.useCallback(async () => {
    const activeStep = activeStepRef.current;
    if (hasNextStep.current) {
      logger.debug('WIZARD', 'goToNextStep' + (activeStep + 1));
      setActiveStep(activeStep + 1);
    } else {
      if (onEndNavigate) {
        logger.debug('WIZARD', 'onEndNavigate()');
        await onEndNavigate(formHandlers);
      }

      // We should now be logged in
      await finishFlow();

      // Last step - exit wizard
      if (endNavigateTo) {
        logger.debug('WIZARD', `Ending wizard. Navigating to ${endNavigateTo}`);
        navigate(endNavigateTo);
      }
    }
  }, [activeStepRef, endNavigateTo, finishFlow, formHandlers, onEndNavigate, setActiveStep]);

  const goToPreviousStep = React.useCallback(() => {
    if (hasPreviousStep.current) {
      logger.debug('WIZARD', 'goToNextStep' + (activeStep - 1));
      setActiveStep(activeStep - 1);
    }
  }, [activeStep, setActiveStep]);

  const jumpToIndex = React.useCallback(
    (stepIndex: number) => {
      if (stepIndex >= 0 && stepIndex < stepCount) {
        setActiveStep(stepIndex);
      } else {
        logger.warn(
          [
            `Invalid step index [${stepIndex}] passed to 'goToStep'. `,
            `Ensure the given stepIndex is not out of boundaries.`,
          ].join('')
        );
      }
    },
    [stepCount, setActiveStep]
  );

  const jumpToPath = React.useCallback(
    (name: string, cb?: () => void) => {
      const index = stepsRef.current.findIndex(
        (child) => (child as React.ReactElement<WizardStepProps>).props.path === name
      );
      jumpToPathCallBack.current = cb;
      jumpToIndex(index);
    },
    [jumpToIndex, stepsRef]
  );

  const registerHandler = React.useCallback((path: string, info: StepHandler) => {
    handlers.current = { ...handlers.current, [path]: info };
  }, []);

  useRunOnce(() => {
    // Restore any session state needed on first load
    // This must run BEFORE hasStepChanged useEffect

    browserStorage.load();
    const lastState = { ...browserStorage.wizardStore };

    if (lastState.base === base && lastState.lastPath && lastState.formData) {
      logger.debug('WIZARD', 'Restoring last state', {
        path: lastState.lastPath,
        data: lastState.formData,
      });

      setTimeout(() => setValue('root', lastState.formData), 100);

      jumpToPath(lastState.lastPath);
      return;
    }

    if (pathName === `${base}/${activePath}`) {
      return;
    }

    // Someone manually navigated to one of the paths
    // which we need to correct here
    navigate(`${base}/${activePath}`);
  });

  const onClickNext = React.useCallback(async () => {
    setIsSubmitting(true);

    const onNext = handlers.current[activePath]?.onNext;
    logger.debug('WIZARD', `Calling onNext for path "${activePath}"`);

    if (!onNext) {
      await goToNextStep();
      return;
    }

    try {
      if (!(await verifyStep())) {
        logger.debug('WIZARD', 'onClickNext - verifyStep failed');
        return;
      }

      await onNext();
    } catch (e) {
      logger.error('Wizard exception', { e });
      setIsSubmitting(false);

      if (!(e as ApolloError).graphQLErrors) {
        // Setting default error
        setError('_', { type: 'error', message: (e as Error).message });

        // If we trigger here we wipe out any error messages
        // await trigger()

        return;
      }

      // Handle Apollo Errors
      const error = buildSubmitError(e as ApolloError);
      const field = (e as any).field ?? '_';
      setError(field, { type: 'error', message: error.errors[field] || error.errors['_'] });

      // If we trigger here we wipe out any error messages
      // await trigger()

      logger.error('Wizard submit error', { field, error });
    }
  }, [activePath, goToNextStep, setError, verifyStep]);

  const activeChildData = useMemoized(activeChild.props.data);
  const hasStepChanged = useHasChangedShallow([activePath]);
  React.useEffect(() => {
    if (!hasStepChanged || activePath === undefined) return;

    logger.debug('WIZARD', 'Step changed', { activeStep, activePath });
    const handler = handlers.current[activePath];
    if (!handler) {
      logger.error(`WIZARD ${base} ERROR: No handler found for path ${activePath}`);
      saveState();
    } else {
      logger.debug('WIZARD', `Changing to use new handler @ ${activePath}`, {
        rules: handler.rules,
        data: handler.data,
      });

      const formSubmitFn = activeChild.props.next === 'submit' ? onClickNext : undefined;
      updateWizardForm(handler.rules, formSubmitFn);
      setStepData({ ...defaultStepData, ...handler.data, ...(activeChildData ?? {}) });

      setTimeout(async () => {
        if (handler?.onActive) {
          logger.debug('WIZARD', 'Step changed - calling onActive');
          void handler?.onActive?.();
        }

        logger.debug('WIZARD', 'Step changed - triggering to reset isValid');
        const valid = await trigger(undefined, { shouldFocus: true });
        if (!valid) {
          logger.debug('WIZARD', 'Step changed - NOT VALID', { errors: formState.errors });
          // Initial state of form is not valid - that's ok - don't show errors yet
          logger.debug('WIZARD', 'Clearing errors');
          clearErrors();
          saveState();
          return;
        }

        logger.debug('WIZARD', 'Step changed - VALID');
        // Do this to set the right state for the Next button
        setValue('id', getValues('id'), { shouldValidate: true });
        jumpToPathCallBack.current?.();
        jumpToPathCallBack.current = undefined;
        saveState();
      }, 500);
    }

    (async function () {
      if (pathNameRef.current !== `${base}/${activePath}`) {
        await navigate(`${base}/${activePath}`);
      }
      setIsSubmitting(false);
    })();
  }, [
    saveState,
    activeChildData,
    activeChild.props.next,
    activePath,
    activeStep,
    base,
    browserStorage,
    clearErrors,
    defaultStepData,
    formState.errors,
    getValues,
    hasStepChanged,
    pathNameRef,
    onClickNext,
    setError,
    setValue,
    trigger,
    updateWizardForm,
  ]);

  const wizardValue = React.useMemo<WizardContextValues>(
    () => ({
      base,
      nextStep: goToNextStep,
      previousStep: goToPreviousStep,
      verifyStep,
      isSubmitting,
      setIsSubmitting,
      onClickNext,
      activeStep,
      activeChild,
      activePath,
      stepCount,
      isFirstStep: !hasPreviousStep,
      isLastStep: !hasNextStep,
      jumpToIndex: jumpToIndex,
      jumpToPath: jumpToPath,
      registerHandler: registerHandler,
      saveState,
      reset,
      finishFlow: finishFlow,
      handlers: handlers.current,
      stepData,
      inModal: Boolean(inModal),
      baseIndex,
    }),
    [
      base,
      baseIndex,
      goToNextStep,
      goToPreviousStep,
      verifyStep,
      isSubmitting,
      onClickNext,
      activeStep,
      activeChild,
      activePath,
      stepCount,
      jumpToIndex,
      jumpToPath,
      registerHandler,
      saveState,
      reset,
      finishFlow,
      stepData,
      inModal,
    ]
  );

  const formRanges = React.useMemo(() => {
    let currentRange = 0;

    const ranges = steps.map((child) => {
      const step = child as React.ReactElement<WizardStepProps & { index: number; id: string }>;
      if (step.props.next === 'submit') {
        currentRange++;
        return currentRange - 1;
      }

      return currentRange;
    });

    logger.debug('WIZARD', 'Wizard form ranges', ranges);
    return ranges;
  }, [steps]);

  const formInRange = formRanges[activeStep];
  const stepChildren = React.useMemo(() => {
    return steps.map((child, index) => {
      const step = child as React.ReactElement<WizardStepProps & { index: number; id: string }>;

      return (
        <AnimateStep key={step.props.path} index={index} path={step.props.path}>
          {React.cloneElement(step, {
            id: `wizard-step-${index}`,
            index,
            render: formRanges[index] === formInRange,
          })}
        </AnimateStep>
      );
    });
  }, [formInRange, formRanges, steps]);

  const onClose = React.useCallback(async () => {
    if (inModal) {
      await finishFlow();
      navigate(base + '/..');
    }
  }, [base, finishFlow, inModal]);

  const Container = React.useCallback<React.FC<React.PropsWithChildren>>(
    ({ children }) => {
      if (!container) return <>{children}</>;

      return React.cloneElement(container, { children });
    },
    [container]
  );

  return (
    <WizardContext.Provider value={wizardValue}>
      {inModal && (
        <Modal.Close
          css={css`
            position: absolute;
            top: 1.5rem;
            right: 1.5rem;
          `}
          onClick={onClose}
        />
      )}
      <Container>
        {before}
        <Wizard.Area className={className} after={after}>
          {stepChildren}
        </Wizard.Area>
      </Container>
    </WizardContext.Provider>
  );
};

export const WizardFlow: React.FC<
  React.PropsWithChildren<WizardProps & FormProps<FieldValues>>
> = ({
  container,
  before,
  after,
  base,
  children,
  startIndex = 0,
  baseIndex = 0,
  inModal,
  defaultStepData,
  onEnd: onEndNavigate,
  onEndNavigateTo: endNavigateTo,
  onGetDefaultValues,
  ...props
}) => {
  const browserStorage = useBrowserStorage();
  const [defaultValues] = React.useState(() => {
    if (browserStorage.wizardStore.base === base) {
      return browserStorage.wizardStore.formData;
    }
    return undefined;
  });

  const [validationSchema, setValidationSchema] = React.useState<
    yup.ObjectSchema<any> | undefined
  >();
  const [onNext, setOnNext] = React.useState<OnNextFn | undefined>();

  const updateWizardForm = React.useCallback<UpdateFormFn>((rules, onNext) => {
    setOnNext((_prev) => {
      return onNext;
    });
    setValidationSchema(rules);

    logger.debug('WIZARD', 'Updating form', { rules, onNext });
  }, []);

  const onSubmit = React.useCallback(async () => {
    logger.debug('WIZARD', 'Submitting form', { onNext });
    if (onNext) {
      // We don't await as onNext handles all the submit logic itself
      void onNext();
    }
  }, [onNext]);

  const def = React.useMemo(() => {
    const values = _.merge({}, props.defaultValues, defaultValues);
    logger.debug('WIZARD', 'Setting default values', { def });
    return onGetDefaultValues ? onGetDefaultValues(values) : values;
  }, [defaultValues, onGetDefaultValues, props.defaultValues]);

  return (
    <ApolloForm
      className='w-full'
      {...props}
      resetStateAfterSubmit={false}
      validationSchema={validationSchema}
      defaultValues={def}
      onSubmit={onSubmit}
    >
      <WizardLogic
        {...{
          container,
          before,
          after,
          base,
          inModal,
          startIndex,
          baseIndex,
          updateWizardForm,
          defaultStepData,
          onEnd: onEndNavigate,
          onEndNavigateTo: endNavigateTo,
        }}
      >
        {children}
      </WizardLogic>
    </ApolloForm>
  );
};

export const WizardFlowModal: React.FC<
  React.PropsWithChildren<
    WizardProps &
      FormProps<FieldValues> & {
        maxHeight?: string;
        minHeight?: string;
        maxWidth?: string;
        minWidth?: string;
      }
  >
> = ({
  className,
  children,
  before: header,
  after: footer,
  minHeight,
  maxHeight,
  minWidth,
  maxWidth,
  ...props
}) => {
  return (
    <Portal containerId='modalContainer'>
      <Modal
        className={className}
        css={css`
          display: flex;
          justify-content: center;
          width: auto !important;
          min-width: ${minWidth ?? 'min(50rem, 100%)'} !important;
          max-width: ${maxWidth ?? 'calc(100% - 2rem)'} !important;
          min-height: ${minHeight ?? '40rem'} !important;
          max-height: ${maxHeight ?? 'calc(100vh - 2rem)'} !important;
          .modal-close {
            z-index: 9999020;
          }
          @media (max-width: 768px) {
            max-height: 100vh !important;
          }
        `}
      >
        <WizardFlow
          {...props}
          before={header ? <Modal.Header>{header}</Modal.Header> : null}
          after={footer ? <Modal.Footer>{footer}</Modal.Footer> : null}
          inModal
        >
          {children}
        </WizardFlow>
      </Modal>
    </Portal>
  );
};

export default WizardFlow;
